import { coldStartAwareFetch, coldStartSleep } from './cold-start.js';
import { checkAssetUrl } from './loader.js';
import { auditScopedCss, scopeAppCssWithRejections } from './scope-css.js';
/**
 * The deadline on one stylesheet fetch, retries and backoff included. The abort signal travels with
 * every attempt `coldStartAwareFetch` makes, and each backoff sleep is cut to the time left before
 * the deadline, so a `Retry-After` longer than the deadline cannot hold a mount in `loading`. The
 * default backoff chain is 0.6 + 1.2 + 2.4 + 4.8 s.
 */
export const CSS_FETCH_TIMEOUT_MS = 20_000;
/** The `data-*` attributes {@link markScopeRoot} writes on a mount element. */
const SCOPE_ATTRIBUTES = ['data-hosted-app', 'data-surface-mount', 'data-surface', 'data-surface-kind'];
const claimed = new Set();
/**
 * The `appId` of the shell's system mount (`SurfaceMountRequest`: a system request carries `appId:
 * 'system'`). Its stylesheet keeps its global names (R-94). The loader resolves every target on app
 * `system` to its own system ref, so no registry app reaches the runtime under this id.
 */
const SYSTEM_APP_ID = 'system';
/**
 * The `@property` registrations in force in this document: decoded name to registration text (R-94).
 * The browser applies the last registration of a name in document order, so the record keeps the last
 * text it saw, never the first (S5a WS-40 security-3). Before each injection,
 * {@link recordDocumentRegistrations} sets the registrations of every stylesheet the document holds,
 * the interim root's `<link>` included (S5a WS-40 security-2). After each injection, the record takes
 * the registrations the new `<style>` puts in force; it is the last element of `document.head`. So an
 * app text that the system stylesheet replaced no longer matches the record, and the app cannot take
 * the name back with a later stylesheet.
 */
const registered = new Map();
/** No registration: the scoper reads the document's stylesheets with an empty record. */
const NO_REGISTRATIONS = new Map();
/**
 * Set in {@link registered} each `@property` registration in `document.styleSheets`, in document order,
 * so the last one of a name is the record, as the browser applies it (R-94, S5a WS-40 security-2 and
 * security-3). A name that no readable stylesheet registers keeps its recorded text.
 * The walk enters each grouping rule (`@media`, `@supports`, `@container`, the block `@layer`) and each
 * `@import`ed stylesheet. The registration text is the scoper's, so a registration in the CSSOM form
 * (`@property --tw-x { syntax: "*"; inherits: false; }`) gives the same text as the minified form
 * Tailwind writes. A stylesheet whose rules the document refuses to show (a cross-origin `<link>`), an
 * `@import` with no stylesheet, and a `<link>` that has not loaded yet add nothing. In a browser with no
 * `CSSPropertyRule` (and so no `@property`) or no `CSSImportRule`, the walk reads no such rule.
 * The walk reads document order only (R-121). It also reads a registration in an `@media` or a
 * `<style media>` that does not match, and a registration in an `@layer`. The browser skips the first
 * two and ranks the third below an unlayered one. WS-40.md D-16 records that residual. Never throws.
 */
function recordDocumentRegistrations() {
    const texts = [];
    for (const sheet of Array.from(document.styleSheets))
        collectRegistrations(rulesOf(sheet), texts);
    const { properties } = scopeAppCssWithRejections(texts.join('\n'), hostedAppSelector(SYSTEM_APP_ID), {
        appId: SYSTEM_APP_ID,
        system: true,
        properties: NO_REGISTRATIONS,
    });
    for (const [name, text] of properties)
        registered.set(name, text);
}
/** The rules of `sheet`, or none when the document refuses to show them (a cross-origin stylesheet throws). */
function rulesOf(sheet) {
    try {
        return Array.from(sheet.cssRules);
    }
    catch {
        return [];
    }
}
/** Append the text of each `@property` rule in `rules` to `texts`, in order, entering grouping rules and imports. */
function collectRegistrations(rules, texts) {
    for (const rule of rules) {
        if (typeof CSSPropertyRule === 'function' && rule instanceof CSSPropertyRule)
            texts.push(rule.cssText);
        else if (rule instanceof CSSGroupingRule)
            collectRegistrations(Array.from(rule.cssRules), texts);
        else if (typeof CSSImportRule === 'function' && rule instanceof CSSImportRule && rule.styleSheet !== null) {
            collectRegistrations(rulesOf(rule.styleSheet), texts);
        }
    }
}
/**
 * Forget every claim and every recorded `@property` registration. `resetSurfaceRuntime` in `./testing`
 * is the only caller; injected `<style>` elements stay.
 */
export function resetCssClaims() {
    claimed.clear();
    registered.clear();
}
/**
 * The selector an app's stylesheet is scoped under: `[data-hosted-app="<appId>"]`, with `"` and
 * `\` escaped so it matches the raw attribute value. The selector legacy apps already use. Pure.
 */
export function hostedAppSelector(appId) {
    return `[data-hosted-app="${appId.replace(/["\\]/g, '\\$&')}"]`;
}
/**
 * Fetch, scope, audit and inject one app's stylesheet, keeping `AppHost.tsx`'s behaviour.
 *
 * - `skipped`: `cssUrl` is `null` or fails `checkAssetUrl`; no request is sent.
 * - `already-present`: this `(appId, url)` pair was claimed before, including by a fetch still in
 *   flight. The claim is taken before the fetch, so concurrent mounts inject once.
 * - `failed`: the response is not 2xx, its content type does not contain `css`, the fetch rejects,
 *   or the {@link CSS_FETCH_TIMEOUT_MS} deadline passes, which includes a backoff sleep reaching
 *   it. The claim is released, so a later mount retries.
 * - `injected`: the text was scoped under {@link hostedAppSelector}, audited, and appended to
 *   `document.head` as one `<style data-hosted-app-css="<appId>">` through `textContent`. When the
 *   audit counts dropped rules, the element carries `data-scope-dropped` with the count and one
 *   console warning names the app, never the URL. Then `onRejected` receives each construct the
 *   scoper refused (R-90), in stylesheet order, once the element is in the head. A throw from
 *   `onRejected` is contained, and the rest still arrive.
 *
 * The scoper confines the document-global names (R-94) with `appId` as the prefix. The system
 * stylesheet (`appId` `system`) keeps its names. Every stylesheet's selectors stop at a nested surface
 * root (R-107). An app `@property` stays only when it equals the registration of its name in force in
 * the document, the last one in document order (S5a WS-40 security-3). Before it scopes, the runtime
 * records every registration in `document.styleSheets` in document order, the interim root's `<link>`
 * and the loader's own CSS included, and then the registrations each injected stylesheet puts in
 * force. The system stylesheet's registrations replace the recorded text of their names. A cross-origin stylesheet and a `<link>` that
 * has not loaded yet add nothing to that record.
 *
 * Only the `injected` outcome calls `onRejected`. The fetch is `coldStartAwareFetch` over the global
 * `fetch`, so a cold asset pod is retried. Never throws and never rejects.
 */
export async function injectScopedCss(appId, cssUrl, onRejected = () => { }) {
    const href = cssUrl === null ? null : checkAssetUrl(cssUrl);
    if (href === null)
        return 'skipped';
    const claim = `${appId}\n${href.href}`;
    if (claimed.has(claim))
        return 'already-present';
    claimed.add(claim);
    try {
        const res = await coldStartAwareFetch(fetch, { sleep: deadlineSleep(Date.now() + CSS_FETCH_TIMEOUT_MS) })(href.href, {
            signal: AbortSignal.timeout(CSS_FETCH_TIMEOUT_MS),
        });
        const contentType = res.headers.get('content-type') ?? '';
        if (!res.ok || !contentType.includes('css')) {
            claimed.delete(claim);
            return 'failed';
        }
        const cssText = await res.text();
        recordDocumentRegistrations();
        const { css: scoped, rejections, properties } = scopeAppCssWithRejections(cssText, hostedAppSelector(appId), {
            appId,
            system: appId === SYSTEM_APP_ID,
            properties: registered,
        });
        for (const [name, text] of properties)
            registered.set(name, text);
        const audit = auditScopedCss(cssText, scoped);
        const style = document.createElement('style');
        style.setAttribute('data-hosted-app-css', appId);
        if (audit.dropped > 0) {
            style.setAttribute('data-scope-dropped', String(audit.dropped));
            console.warn(`[sdk-surfaces] the CSS scoper dropped ${audit.dropped} of ${audit.rulesIn} style rule(s) for '${appId}'. ` +
                `It refused ${rejections.length} construct(s), each reported as a surface:css-rejected event (R-90); ` +
                'a dropped rule with no such event is a runtime bug, not an app bug (#1292).');
        }
        style.textContent = scoped;
        document.head.append(style);
        for (const rejection of rejections) {
            try {
                onRejected(rejection);
            }
            catch {
                console.error(`[sdk-surfaces] a css-rejected listener threw for '${appId}'; the other rejections still arrive`);
            }
        }
        return 'injected';
    }
    catch {
        claimed.delete(claim);
        return 'failed';
    }
}
/**
 * The runtime's backoff timer, bounded by `deadline` (epoch ms): each sleep lasts at most the time
 * left, and once the deadline has passed the sleep rejects, which ends the retry loop.
 */
function deadlineSleep(deadline) {
    const sleep = coldStartSleep();
    return async (ms) => {
        await sleep(Math.min(ms, Math.max(0, deadline - Date.now())));
        if (Date.now() >= deadline)
            throw new Error('the stylesheet deadline passed');
    };
}
/**
 * Mark `el` as one mount's scope root: `data-hosted-app` = `ref.appId` and `data-surface-mount` =
 * `mountId`, plus `data-surface` (the name) and `data-surface-kind` for a declared ref. Returns a
 * function that puts back every attribute's prior value, removing those that were absent; calling
 * it twice is harmless. Touches no other attribute.
 */
export function markScopeRoot(el, ref, mountId) {
    const prior = SCOPE_ATTRIBUTES.map((name) => [name, el.getAttribute(name)]);
    el.setAttribute('data-hosted-app', ref.appId);
    el.setAttribute('data-surface-mount', mountId);
    if (ref.declaration !== null) {
        el.setAttribute('data-surface', ref.declaration.name);
        el.setAttribute('data-surface-kind', ref.declaration.kind);
    }
    return () => {
        for (const [name, value] of prior) {
            if (value === null)
                el.removeAttribute(name);
            else
                el.setAttribute(name, value);
        }
    };
}
//# sourceMappingURL=css.js.map